/* eslint-disable no-secrets/no-secrets */
import { ZenitClickAction } from '../models/events/click.model';
import { ZenitExperiment, ZenitExperiments } from '../models/events/page-hit-event.model';
import {
    ZenitList,
    Section,
    ZenitEventData,
    ZenitEventName,
    ZenitEventValues,
    ZenitItem,
    ZenitPageType,
    ZenitPersonalizationParam,
    ZenitZoomLevel,
} from '../models/zenit-general.model';

import { CartItem } from '@api/cart/cart.models';
import { GiftCard } from '@api/gift-card/models/gift-card.models';
import { CommonWindow } from '@core/domain/models/common-window.model';
import { CurrencyInfo } from '@core/domain/models/store.models';
import { PageSection } from '@core/domain/types/page-sections.types';
import { ProductPublicModels } from '@features/products/product';
import { SidebarCustomComponents } from '@features/sidebar';
import { GridElement } from '@pages/category/modules/category-grid/public_api';
import { LIMIT_QUERY_SEARCH, QueryResult, SearchType } from '@pages/search';
import { OptimizelyData, OptimizelyState, OptimizelyCampaignsState, OptimizelyExperiment } from '@shared/ab-testing/models/optimizely.models';
import { EventFrom, ItemTypes } from '@shared/analytics/COMMON/models/analytics-common.models';
import { eventFromParser } from '@shared/analytics/COMMON/services/analytics.utils';
import * as AnalyticsUtils from '@shared/analytics/COMMON/services/analytics.utils';
import { ViewItemProduct } from '@shared/analytics/COMMON/services/product-view/view-item.service';
import { SearchRequestMethodName } from '@shared/analytics/store/analytics.state';
import { SearchRequestFilter } from '@shared/analytics/ZENIT/models/events/search-assistant.model';
import { GridType, GridTypes } from '@shared/models/grid-type.model';
import { ProductViewPlace, ProductViewPlaceValue } from '@shared/product/models';
import { ProductUid } from '@shared/product/related-products/domain/models/related-products.models';
import { RelatedsPlace } from '@shared/product/related-products/infrastructure/state/related-products.state';

// #region Modelos

interface OriginDataParams {
    place?: ProductViewPlace;
    searchRequestQuery?: string;
    searchType?: SearchType;
}

// #endregion

// #region Constantes

const RelatedPlaces = [
    EventFrom.backSoon,
    EventFrom.cartYoda,
    EventFrom.comingSoon,
    EventFrom.completeLook,
    EventFrom.completeLookYoda,
    EventFrom.frecuentlyBoughtTogether,
    EventFrom.othersYouMayLike,
    EventFrom.recentlyViewed,
    EventFrom.recommended_for_you,
    EventFrom.relateds,
    EventFrom.relatedsBuyed,
    EventFrom.similar,
    EventFrom.similaresYoda,
    EventFrom.topSales,
    EventFrom.wishListRelated,
];

const HELP_PAGES_REGEX = /\/(help)(\/\w*)*/;

// #endregion

// #region Índice de item
/* ## Funciones para calcular el índice de un item en listas ## */

export const getQueryResultsIndex = <ItemType>(
    item: ItemType & { id?: number | string; productId?: string },
    queryResults: QueryResult[],
): number => {
    return queryResults?.find(product => product.id === item?.id)?.position;
};

export const getItemListIndex = <ItemType>(
    item: ItemType & { id?: number | string; productId?: string },
    itemList: Array<ItemType & { id?: number | string; productId?: string }> = []
): number => {
    const itemId = typeof item?.id === 'number' ? item.id?.toString() : item?.id;
    const foundItem = itemList.find((_item) =>
        (_item?.id?.toString() || _item.productId) === (itemId || item?.productId));
    return itemList.indexOf(foundItem);
};

export const getRelatedProduct = (
    item: { id?: number | string; productId?: string; moca?: string },
    place: ProductViewPlace,
    relatedsSectionId: string,
    relatedProductsPlaces: Record<ProductViewPlace, RelatedsPlace>
): ProductUid => {
    const carousel = relatedProductsPlaces?.[place]
        ?.carousels?.[relatedsSectionId];
    return carousel?.filteredItemsIds.find((prodUid) => {
        return item.id === prodUid?.product_id || item.moca === prodUid?.uid?.slice(1);
    });
};

export const getRelatedProductIndex = (
    item: { id?: number | string; productId?: string; moca?: string },
    place: ProductViewPlace,
    relatedsSectionId: string,
    relatedProductsPlaces: Record<ProductViewPlace, RelatedsPlace>
): number => {
    const carousel = relatedProductsPlaces?.[place]
        ?.carousels?.[relatedsSectionId];
    return carousel?.filteredItemsIds.findIndex((prodUid) => {
        return item.id === prodUid?.product_id || item.moca === prodUid?.uid?.slice(1);
    });
};

export const getProductItemIndex = <ItemType>(
    item: ItemType & { id?: number | string; productId?: string },
    eventFrom: EventFrom,
    values: ZenitEventValues,
    eventData: ZenitEventData
): number | null => {
    let index: number;
    switch (eventFrom) {
        case EventFrom.search:
        case EventFrom.searchResults:
            return getQueryResultsIndex(item, values.queryResults);

        case EventFrom.parrilla:
            index = AnalyticsUtils.getProductCategoryIndex(
                undefined,
                item as unknown as ProductPublicModels.PublicProduct,
                values.filteredProductsIds,
                values.productsArray,
            );
            break;

        case EventFrom.productdetail:
            return null;

        case EventFrom.shopcart:
            index = getItemListIndex<ItemType>(item, values.cartItems as unknown as ItemType[]);
            break;

        case EventFrom.wishList:
        case EventFrom.miniWishList:
            index = getItemListIndex<ItemType>(item, values.wishListItems as unknown as ItemType[]);
            break;

        case EventFrom.fichaBundle:
            index = getItemListIndex<ItemType>(item, values?.productPageProduct.bundleProducts as unknown as ItemType[]);
            break;

        case EventFrom.cartYoda:
        case EventFrom.completeLook:
        case EventFrom.completeLookYoda:
        case EventFrom.frecuentlyBoughtTogether:
        case EventFrom.othersYouMayLike:
        case EventFrom.recentlyViewed:
        case EventFrom.recommended_for_you:
        case EventFrom.relateds:
        case EventFrom.relatedsBuyed:
        case EventFrom.similar:
        case EventFrom.similaresYoda:
        case EventFrom.topSales:
        case EventFrom.wishListRelated:
            index = getRelatedProductIndex(
                item,
                eventData?.place,
                eventData?.relatedsSectionId,
                values?.relatedProductsPlaces
            );
            break;

        default:
            index = item && values.items ? getItemListIndex<ItemType>(item, values.items as unknown as ItemType[]) : -1;
            break;
    }
    return index === -1 ? null : index + 1;
};

// #endregion

// #region pageType

export const getCmsPagePageType = (currentUrl: string): ZenitPageType => {
    if (HELP_PAGES_REGEX.test(currentUrl)) {
        return ZenitPageType.HELP_CENTER;
    }
    return ZenitPageType.CATEGORY_VIEW;
};

export const getPageTypeForSearch = (
    eventData: ZenitEventData,
    values: ZenitEventValues,
    eventFrom: EventFrom,
    eventName: ZenitEventName,
): ZenitPageType => {
    if (eventName === ZenitEventName.SearchAssistant) return ZenitPageType.SEARCH_REQUEST;
    if (values?.searchRequestQuery === '') return ZenitPageType.PRODUCT_SEARCH_START;

    if ([EventFrom.search, EventFrom.searchResults].includes(eventFrom)
        // eslint-disable-next-line @typescript-eslint/no-extra-parens
        || (values?.searchRequestQuery !== '' && !eventData?.totalHits)) {
        return ZenitPageType.PRODUCT_SEARCH_RESULTS;
    }
    return undefined;
};

export const getPageTypeByPage = (
    values: ZenitEventValues,
    eventData: ZenitEventData,
    eventFrom: EventFrom,
    eventName: ZenitEventName,
): ZenitPageType => {
    // Standard cases
    switch (values?.page) {
        case 'ItxAngularAuthenticationActivateFeelUserPage':
            return ZenitPageType.USER_LOYALTY_CHECK;
        case 'ItxAngularAuthenticationActivateUserPage':
            return ZenitPageType.USER_LOYALTY_TICKET;
        case 'ItxAngularAuthenticationConfirmationPage':
            return ZenitPageType.USER_REGISTRATION_CONFIRMATION;
        case 'ItxAngularAuthenticationPage':
            return ZenitPageType.USER_LOGON;
        case 'ItxAngularAuthenticationRecoveryPage':
            return values?.currentUrl?.includes('#resetPassword') ? ZenitPageType.PASSWORD_RECOVERY_RESET : ZenitPageType.PASSWORD_RECOVERY;
        case 'ItxAngularAuthenticationRegisterPage':
            return ZenitPageType.USER_REGISTRATION;
        case 'ItxAngularCmsRenderPage':
            return getCmsPagePageType(values?.currentUrl);
        case 'ItxAngularGiftCardActivationPage':
            return ZenitPageType.E_GIFTCARD_ACTIVATION;
        case 'ItxAngularGiftCardBalancePage':
            return ZenitPageType.E_GIFTCARD_BALANCE;
        case 'ItxAngularGiftCardPage':
            return ZenitPageType.GIFTCARD;
        case 'ItxAngularOrderConfirmationPage':
        case 'ItxAngularOrderConfirmationNodePage':
        case 'ItxOrderConfirmationPage':
            return ZenitPageType.PURCHASE_ORDER_END;
        case 'ItxAngularOrderStatus':
            return ZenitPageType.ORDER_TRACK;
        case 'ItxSearchPage':
            return getPageTypeForSearch(eventData, values, eventFrom, eventName);
        case 'ItxAngularShopcartPage':
            return values.cartItems?.length ? ZenitPageType.SHOPPING_CART : ZenitPageType.SHOPPING_CART_EMPTY;
        case 'ItxAngularStoreDetailLocatorPage':
            return ZenitPageType.STORE_DETAIL;
        case 'ItxAngularStoreLocatorPage':
            return ZenitPageType.SHOP_LOCATOR;
        case 'ItxAngularWishlistPage':
            return ZenitPageType.MULTI_WISHLIST;
        case 'ItxAngularLookbookPage':
            return ZenitPageType.CATEGORY_VIEW;
        case 'ItxCategoryPage':
            return ZenitPageType.PRODUCT_LIST;
        case 'ItxChatPage':
            return ZenitPageType.CHAT;
        case 'ItxCheckoutShippingPage':
            return ZenitPageType.CHECKOUT_SHIPPING_METHOD;
        case 'ItxCheckoutShippingDeliveryPage':
            return ZenitPageType.CHECKOUT_HOME_ADDRESS_LIST;
        case 'ItxCheckoutShippingDeliveryAddressPage':
            return values.isSelectedAddress
                ? ZenitPageType.CHECKOUT_MODIFY_PERSONAL_DETAILS : ZenitPageType.CHECKOUT_ADD_PERSONAL_DETAILS;
        case 'ItxCheckoutShippingDeliverySummaryPage':
            return ZenitPageType.CHECKOUT_TYPE_DELIVERY_METHOD;
        case 'ItxCheckoutShippingStoreSummaryPage':
            return ZenitPageType.CHECKOUT_STORE_DETAIL;
        case 'ItxCheckoutShippingDropPointSummaryPage':
            return ZenitPageType.CHECKOUT_DROP_POINT_DETAIL;
        case 'ItxCheckoutShippingStorePage':
            return values.selectedViewPickup === 'map' ? ZenitPageType.CHECKOUT_STORE_COLLECTION_POINT_MAP : ZenitPageType.CHECKOUT_STORE_LIST;
        case 'ItxCheckoutShippingDropPointPage':
            return values.selectedViewPickup === 'map' ? ZenitPageType.CHECKOUT_DROP_POINT_MAP : ZenitPageType.CHECKOUT_DROP_POINT_LIST;
        case 'ItxCheckoutPaymentPage':
            return ZenitPageType.CHECKOUT_PAYMENT_METHOD;
        case 'ItxCheckoutSummaryPage':
            return ZenitPageType.CHECKOUT_ORDER_REVIEW;
        case 'ItxCheckoutRepaymentPage':
            return ZenitPageType.CHECKOUT_REPAYMENT;
        case 'ItxAngularContactPage':
            return ZenitPageType.CONTACT;
        case 'ItxFastSintStoreListPage':
            return ZenitPageType.CHECKOUT_EXPRESS_STORE_LIST;
        case 'ItxFastSintStoreDetailPage':
            return ZenitPageType.CHECKOUT_EXPRESS_STORE_DETAIL;
        case 'ItxHomePage':
        case 'ItxHomeMenPage':
            return ZenitPageType.HOME;
        case 'ItxMassimoDuttiFeelPage':
            return ZenitPageType.HOME_LOYALTY;
        case 'ItxErrorPage':
        case 'Itx404ErrorPage':
        case 'Itx410ErrorPage':
            return ZenitPageType.PAGE_NOT_FOUND;
        case 'ItxMyAccountAccessDataPage':
            return ZenitPageType.MY_ACCOUNT_ACCESS_DETAILS;
        case 'ItxMyAccountAddressListPage':
            return ZenitPageType.MY_ACCOUNT_ADDRESSES;
        case 'ItxMyAccountAppointmentsPage':
            return ZenitPageType.MY_ACCOUNT_APPOINTMENTS;
        case 'ItxMyAccountCardsPage':
            return ZenitPageType.MY_ACCOUNT_WALLET;
        case 'ItxMyAccountDetailsPage':
            return ZenitPageType.MY_ACCOUNT_PERSONAL_DETAILS;
        case 'ItxMyAccountFeelBenefitsPage':
            return ZenitPageType.MY_ACCOUNT_LOYALTY_BENEFITS;
        case 'ItxMyAccountFeelHistoryPage':
            return ZenitPageType.MY_ACCOUNT_LOYALTY_HISTORY;
        case 'ItxMyAccountFeelHowItWorksPage':
            return ZenitPageType.MY_ACCOUNT_LOYALTY_HOW_IT_WORKS;
        case 'ItxMyAccountMenuPage':
            return ZenitPageType.MY_ACCOUNT_MENU;
        case 'ItxMyAccountNewsletterPage':
            return ZenitPageType.MY_ACCOUNT_NEWSLETTER;
        case 'ItxMyAccountOrderExchanges':
            return ZenitPageType.MY_ACCOUNT_ORDER_RETURNS_EXCHANGES_ITEMS;
        case 'ItxMyAccountOrderReturnableProducts':
            return ZenitPageType.RETURNABLE_PRODUCTS_SELECTOR;
        case 'ItxMyAccountOrdersOnline':
            return ZenitPageType.MY_ACCOUNT_ORDERS_LIST_ONLINE;
        case 'ItxMyAccountOrdersStore':
            return ZenitPageType.MY_ACCOUNT_ORDERS_LIST_IN_STORE;
        case 'ItxMyAccountReturnItemsPage':
            return ZenitPageType.RETURNABLE_PRODUCTS_SELECTOR;
        case 'ItxMyAccountReturnsPage':
            return ZenitPageType.MY_ACCOUNT_RETURNS_LIST;
        case 'ItxNewsletterPage':
            return ZenitPageType.NEWSLETTER_SUBSCRIBE_VIEW;
        case 'ItxNewsletterUnsuscribePage':
            return ZenitPageType.NEWSLETTER_UNSUBSCRIBE_VIEW;
        case 'ItxProductPage':
            return values.productPageProduct?.type === 'BundleBean'
                ? ZenitPageType.PRODUCT_BUNDLE_DETAILS
                : ZenitPageType.PRODUCT_DETAILS;
        case 'ItxStoriesPage':
            return ZenitPageType.CATEGORY_VIEW;
        case 'ItxWorldWidePage':
            return ZenitPageType.WORLDWIDE;
        default:
            return ZenitPageType.UNKNOWN;
    }
};

export const getPageType = (
    values: ZenitEventValues,
    eventFrom: EventFrom,
    eventData: ZenitEventData,
    eventName: ZenitEventName,
): ZenitPageType => {
    // Special cases
    if (eventFrom === EventFrom['shop-cart-packagin']) {
        return ZenitPageType.GIFT_OPTIONS;
    }
    if (eventData?.place === ProductViewPlaceValue.CS_BS) return ZenitPageType.COMING_SOON_CONFIRMATION;

    // Page type by page and values
    return getPageTypeByPage(values, eventData, eventFrom, eventName);
};

// #endregion

export const getSection = (section: string, commonWindow?: CommonWindow): Section => {
    if (section) return Section[section] || Section.UNKNOWN;

    const lastSection = commonWindow.localStorage.getItem('LAST_SECTION');
    return Section[lastSection] || Section.UNKNOWN;
};

/**
 * Identifier of the category
 * NOTE: If value cannot be sent use a code value of -1
 *
 * @param page
 * @param categoryId
 * @param categoryProductId
 * @returns number
 */
export const getCategoryId = (categoryId: string): number => {
    return categoryId ? +categoryId : -1; // Added + for number format
};

export const getCategoryURL = (categoryUrl: string): string => {
    const categoryUrlSplit = categoryUrl ? categoryUrl.split('-') : undefined;
    return categoryUrlSplit ? categoryUrlSplit[categoryUrlSplit.length - 1] : null;
};

export const getCatentryId = (item: ItemTypes): number => {
    if (!item) {
        return null;
    }

    if ('catentryId' in item) return item.catentryId;

    // Caso ficha bundle
    if ((item as ProductPublicModels.PublicProduct)?.type === 'BundleBean') {
        return Number.parseInt((item as ProductPublicModels.PublicProduct).id);
    }

    if ('status' in item && item.status?.selectedColor?.catentryId) {
        return item.status.selectedColor.catentryId;
    }

    return null;
};

export const getPartNumber = (
    item: ItemTypes | GiftCard
): string | null => {
    return (
        (item as ProductPublicModels.PublicProduct)?.status || (item as CartItem)
    )?.selectedSize?.partnumber || (item as GiftCard)?.partnumber
        || (item as CartItem)?.reference || null;
};

export const getReferenceStyle = (item: ItemTypes | GiftCard): string | -1 => {
    const itemStatus = (item as ProductPublicModels.PublicProduct)?.status;

    if (itemStatus) return itemStatus?.selectedColor?.reference;
    if ((item as CartItem)?.reference) {
        const referenceSplit = (item as CartItem).reference.split('-');
        const mocacoStyle = referenceSplit[0]?.substring(0, 11);

        if (mocacoStyle) {
            return `C${mocacoStyle}-${referenceSplit[1]}`;
        }
    }

    return (item as GiftCard)?.partnumber || -1;
};

export const getSkus = (
    items: ItemTypes[] | GiftCard[]
): string[] => {
    return items.map((item) => getPartNumber(item));
};

export const getCartId = (cartId: number | '-1'): number => {
    return cartId === '-1' ? null : cartId;
};

export const getStoreId = (storeId: string): number => {
    return storeId ? Number.parseInt(storeId) : null;
};

export const getCurrency = (currency: CurrencyInfo): string => {
    return currency?.currencyCode;
};

export const getItems = (items: CartItem[]): ZenitItem[] => {
    return items?.map((item: CartItem): ZenitItem => {
        return {
            amount: item?.unitPrice * item?.quantity,
            articleBrandCode: 11,
            brandId: 11,
            price: item?.unitPrice,
            section: getSection(item?.sectionNameEN),
            sku: getSkus([item])?.[0],
            units: item?.quantity,
            viewOrigin: null, // No tenemos este dato por ahora
        };
    });
};

export const getZoomLevel = (gridType: GridType): ZenitZoomLevel => {
    return {
        1: ZenitZoomLevel.ZOOM1,
        2: ZenitZoomLevel.ZOOM2,
        3: ZenitZoomLevel.ZOOM3,
    }[gridType];
};

export const getPersonalization = (
    item: ItemTypes,
    relatedProductsPlaces: Record<ProductViewPlace, RelatedsPlace>,
    place: ProductViewPlace,
    relatedsSectionId: string
): ZenitPersonalizationParam => {
    const relatedsData = getRelatedProduct(item, place, relatedsSectionId, relatedProductsPlaces);
    return {
        objective: relatedsData?.modelName,
        provider: relatedsData?.provider,
        strategy: relatedsData?.relatedType,
        version: relatedsData?.modelVersion,
    };
};

export const getQuantity = (eventQuantity: number, item: ItemTypes): number => {
    if (eventQuantity) return eventQuantity;
    if ('quantity' in item) return item.quantity;
    return undefined;
};

// #region Listas

export const getListForSearch = (originData: OriginDataParams, eventFrom: EventFrom): ZenitList => {
    if (RelatedPlaces.includes(eventFrom)
        && originData?.searchRequestQuery) return ZenitList.searchAlternativeResults;
    if (RelatedPlaces.includes(eventFrom)) return ZenitList.searchCrossCustom;

    switch (originData?.searchType) {
        case 'busqueda_relacionada':
        case 'busqueda_sugerida':
        case 'trending':
        case 'ultimas_busquedas':
            return ZenitList.suggestedProduct;
        default:
            return ZenitList.searchResults;
    }
};

export const getListForRelateds = (
    page: PageSection,
    eventFromParsed: EventFrom,
    sidebarPlace: EventFrom,
    place: Partial<ProductViewPlace>,
): ZenitList => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (place) {
        case ProductViewPlaceValue.ADD_CART:
            return ZenitList.crossCustomToast;

        case ProductViewPlaceValue.CART:
            return ZenitList.crossSellingCart;

        case ProductViewPlaceValue.CS_BS:
            return {
                [EventFrom.backSoon]: ZenitList.crossSimilarBackSoon,
                [EventFrom.comingSoon]: ZenitList.crossSimilarComingSoon,
            }[sidebarPlace];

        case ProductViewPlaceValue.OUT_OF_STOCK: {
            const listParser = {
                [EventFrom.productdetail]: ZenitList.crossSimilarPdp,
                ItxCategoryPage: ZenitList.crossSimilarGrid,
                ItxProductPage: ZenitList.crossSimilarCustomPdp,
            };
            return listParser[sidebarPlace] || listParser[page] || ZenitList.noListUndefined;
        }

        case ProductViewPlaceValue.PRODUCT_GRID:
            return ZenitList.crossSimilarGrid;
    }

    const crossSelling: Record<PageSection, ZenitList> = {
        ItxProductPage: ZenitList.crossCustomPdp,
    };

    return {
        [EventFrom.backSoon]: ZenitList.crossSimilarBackSoon,
        [EventFrom.comingSoon]: ZenitList.crossSimilarComingSoon,
        [EventFrom.completeLook]: crossSelling[page],
        [EventFrom.completeLookYoda]: crossSelling[page],
        [EventFrom.frecuentlyBoughtTogether]: crossSelling[page],
        [EventFrom.othersYouMayLike]: ZenitList.mayAlsoLike,
        [EventFrom.recentlyViewed]: ZenitList.crossViewedRecently,
        [EventFrom.recommended_for_you]: crossSelling[page],
        [EventFrom.relateds]: crossSelling[page],
        [EventFrom.relatedsBuyed]: crossSelling[page],
        [EventFrom.similar]: crossSelling[page],
        [EventFrom.topSales]: crossSelling[page],
        [EventFrom.wishListRelated]: crossSelling[page],
    }[eventFromParsed] || ZenitList.noListUndefined;
};

export const getListForPage = (
    eventFrom: EventFrom,
): ZenitList => {
    switch (eventFrom) {
        case 'parrilla':
        case 'giftcard':
            return ZenitList.grid;

        case 'ficha_bundle':
        case 'vista_rapida_bundle':
            return ZenitList.bundle;

        case 'miniWishList':
        case 'wishList':
            return ZenitList.wishlist;

        case 'shopcart':
        case 'miniCart':
            return ZenitList.shoppingCart;

        case 'directo':
            return ZenitList.noListUndefined;

        case 'search':
        case 'searchResults':
            return ZenitList.searchResults;

        case 'productdetail':
            return null;

        default:
            return ZenitList.noListUndefined;
    }
};

export const getList = (
    eventFrom: EventFrom,
    page: PageSection,
    item: ItemTypes,
    originData: OriginDataParams,
    sidebarData: { name: SidebarCustomComponents; data: any }
): ZenitList => {
    const { eventFromParsed, sidebarPlace } = eventFromParser(
        eventFrom,
        item,
        sidebarData,
    );

    if (originData?.place === ProductViewPlaceValue.SEARCH) return getListForSearch(originData, eventFromParsed);
    if (RelatedPlaces.includes(eventFromParsed)) {
        return getListForRelateds(page, eventFromParsed, sidebarPlace, originData?.place);
    }

    return getListForPage(eventFrom);
};

// #endregion

// #region Experiments
// ############### EXPERIMENTS PROPERTY LOGIC ###############

export const getOptimizelyExperimentCampaignId = (campaigns: OptimizelyCampaignsState, experimentId: string): string => {
    return Object.values(campaigns).find((campaing) => {
        return campaing.experiment.id === experimentId;
    })?.id;
};

export const formOptimizelyExperimentObject = (
    experiment: OptimizelyExperiment,
    data: OptimizelyData,
    campaigns: OptimizelyCampaignsState,
    experimentId: string,
): ZenitExperiment => {
    const optimizelyExperimentCampaignId = getOptimizelyExperimentCampaignId(campaigns, experimentId);

    return {
        experimentDetails: {
            experimentAccountId: Number.parseInt(data.accountId),
            experimentCampaignId: Number.parseInt(optimizelyExperimentCampaignId),
            experimentId: Number.parseInt(experiment.id),
            experimentProjectId: Number.parseInt(data.projectId),
        },
        experimentKey: experiment.experimentName,
        experimentName: experiment.experimentName,
        provider: 'optimizely',
        variationKey: experiment.variation.name,
    };
};

export const getOptimizelyExperiments = (commonWindow: CommonWindow): ZenitExperiment[] => {
    if (!commonWindow?.optimizely?.initialized) {
        return [];
    }

    const data = commonWindow.optimizely.get('data') as OptimizelyData;
    const state = commonWindow.optimizely.get('state') as OptimizelyState;
    const campaigns = state.getCampaignStates({ isActive: true });
    const stateExperiments = state.getExperimentStates({ isActive: true });

    return Object.entries(stateExperiments).reduce((acc: ZenitExperiment[], [experimentId, val]) => {
        acc.push(formOptimizelyExperimentObject(val, data, campaigns, experimentId));
        return acc;
    }, []);
};

/** Si en el futuro se incluyen más proveedores se añadirán aquí */
export const getExperiments = (commonWindow: CommonWindow): ZenitExperiments => {
    const optimizely = getOptimizelyExperiments(commonWindow);

    return { optimizely };
};

// #endregion

/**
 * Si no hay definida una recomendación de talla se devuelve null.
 * Si el producto no contiene un objeto de status se devuelve null.
 * Si el evento no es AddToCart o Click con action de tipo "select-size" se devuelve null.
 * Si las condiciones previas se cumplen se devuelve el resultado de la comparación entre la talla del producto y la talla recomendada.
 */
export const getIsRecommended = (
    eventName: ZenitEventName,
    eventAction: ZenitClickAction,
    product: ItemTypes,
    recommendedSize: string,
): string | null => {
    if (!recommendedSize) return null;
    if (!('status' in product)) return null;
    if (
        eventName !== ZenitEventName.AddToCart
        && !(eventName === ZenitEventName.Click && eventAction === ZenitClickAction['select-size'])
    ) return null;

    return (product?.status?.selectedSize?.name === recommendedSize).toString();
};

export const getRecommender = (eventName: ZenitEventName, eventAction: ZenitClickAction, recommendedSize: string) => {
    if (
        eventName !== ZenitEventName.AddToCart
        && !(eventName === ZenitEventName.Click && eventAction === ZenitClickAction['select-size'])
    ) return null;

    return recommendedSize ? 'sastre' : null;
};

// #region Search request

export const getSearchRequestFilters = (_filters: string): SearchRequestFilter[] => {
    if (!_filters) {
        return [];
    }

    const filters: Record<string, string[]> = _filters.split(',').reduce((acc, curr) => {
        const [_key, value] = curr.split(':');
        const key = _key.trim();

        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(value);
        return acc;
    }, {});

    return Object.entries(filters).map(([name, values]) => ({
        name: name.split('_')?.[0].toUpperCase(),
        values,
        numElements: values.length,
    }));
};

export const getSearchRequestOffset = (searchRequestOffset = 0): number => {
    if (searchRequestOffset === 0) {
        return 0;
    }

    return searchRequestOffset - LIMIT_QUERY_SEARCH;
};

export const getSearchRequestParams = (
    param: string,
    values: ZenitEventValues,
    eventData: ZenitEventData,
): number | string | SearchRequestFilter[] | null => {
    switch (param) {
        case 'searchRequestResponseTime':
            return eventData.responseTime ?? null;
        case 'searchRequestResultsNum': {
            const resultsNum = Number.parseInt(eventData?.totalHits?.toString());
            return Number.isNaN(resultsNum) ? null : resultsNum;
        }
        case 'searchRequestMethodName':
            return SearchRequestMethodName.query;
        case 'searchRequestLimit':
            return LIMIT_QUERY_SEARCH;
        case 'searchRequestHTTPResponseCode':
            return eventData.responseCode ?? -1;
        case 'searchRequestOffset':
            return getSearchRequestOffset(values.searchRequestOffset);
        case 'searchRequestProvider':
            return eventData.provider;
        case 'searchRequestPass':
            return eventData.searchPass;
        case 'searchRequestFilters':
            return getSearchRequestFilters(values.searchRequestFilters);
    }
};

// #endregion

export const getItemMediaExtraInfoProperty = (item: ViewItemProduct | ItemTypes, property: string): string => {
    const idMedia = (item as ViewItemProduct)?.idMedia;
    if (!idMedia) return null;

    try {
        for (const medias of item?.xmedia as any[]) {
            for (const xmediaItem of medias?.xmediaItems as any[]) {
                const foundPropertyValue = xmediaItem?.medias?.find((media) => {
                    return media?.idMedia === idMedia;
                })?.extraInfo?.[property];

                if (foundPropertyValue) {
                    return foundPropertyValue;
                }
            }
        }
        return null;
    } catch {
        return null;
    }
};

export const getTemplateType = (
    targetItem: ItemTypes,
    gridType: GridType,
    gridElements: GridElement[]
): string => {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (gridType) {
        case GridTypes.RELATEDS:
        case GridTypes.SEARCH:
            return null;

        case GridTypes.ZARA_STUDIO: {
            const productId = typeof targetItem?.id === 'string' ? Number.parseInt(targetItem.id) : targetItem?.id;
            return gridElements?.find((gridElement) => gridElement?.ccIds?.includes(productId))?.template || null;
        }

        case GridTypes.GRID_4:
            return 'GRID_TYPE_2';

        case GridTypes.GRID_16:
            return 'GRID_TYPE_3';
    }
};
